import { defineStore } from "pinia";
import { publicBoaHttp, honoClient } from "@/boa-api-client";
import fixedEncodeURIComponent from "@/util/fixed-encode-uri-component";
import { HTTPError } from "ky";
import type {
  Ticket,
  User,
  UpdateContactOptions,
  CreateContactOptions,
  DeleteContactOptions,
} from "best-of-awards-api";

export type Contact = {
  GSI1_PK: string;
  GSI1_SK: string;
  GSI2_PK: string;
  GSI2_SK: string;
  PK: string;
  SK: string;
  id: string;
  firstName: string;
  lastName: string;
  mobile: string;
  emailAddress: string;
  emailOptIn: boolean;
  smsOptIn: boolean;
  title?: string;
  user?: User;
  tickets: Array<Ticket>;
};

type State = any;

export default defineStore("contacts", {
  state: (): State => ({}),
  getters: {},
  actions: {
    async getContact(key: string) {
      const response = await honoClient.contacts[":key"].$get({
        param: {
          key: fixedEncodeURIComponent(key),
        },
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const contact = await response.json();
      return contact;
    },
    async createContact(contact: CreateContactOptions) {
      const response = await honoClient.contacts.$put({
        json: contact,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const newContact = await response.json();
      return newContact;
    },
    async updateContact(contact: UpdateContactOptions) {
      const response = await honoClient.contacts.$post({
        json: contact,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const updatedContact = await response.json();
      return updatedContact;
    },
    async deleteContact(contact: DeleteContactOptions) {
      const response = await honoClient.contacts.$delete({
        json: contact,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const updatedContact = await response.json();
      return updatedContact;
    },
    async searchContacts({ queryText = "", pageNum = 1 }) {
      const response = await honoClient.contacts.$get({
        query: {
          queryText,
          pageNum: `${pageNum}`,
        },
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const { results, total } = await response.json();
      return { results, total };
    },
    async unsubscribe(email: string) {
      try {
        if (email) {
          const response = await publicBoaHttp
            .get(`unsubscribe/${email}`)
            .json<{
              success: boolean;
              cannotUnsubscribe?: boolean;
              message?: string;
            }>();
          return response;
        }
        return { success: true };
      } catch (error) {
        if (error instanceof HTTPError) {
          const { message = "Unknown error" } = await error.response.json();
          return { success: false, message };
        }
        return { success: false, message: "Unknown error" };
      }
    },
    clear() {
      this.$reset();
    },
  },
});
